import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { getSponsors } from "@/lib/api";
import { useOutletContext } from "react-router-dom";

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        const response = await getSponsors();
        setSponsors(response.data);
      } catch (error) {
        console.error("Error fetching sponsors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSponsors();
  }, []);

  const goldSponsors = sponsors.filter(s => s.tier === "gold");
  const silverSponsors = sponsors.filter(s => s.tier === "silver");
  const bronzeSponsors = sponsors.filter(s => s.tier === "bronze");

  const SponsorCard = ({ sponsor, size = "normal" }) => (
    <a 
      href={sponsor.website_url || "#"} 
      target="_blank" 
      rel="noopener noreferrer"
      className="block"
    >
      <Card className={`card-hover overflow-hidden ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <CardContent className={`flex flex-col items-center justify-center ${size === "large" ? "p-8" : "p-6"}`}>
          {sponsor.logo_url ? (
            <img 
              src={sponsor.logo_url} 
              alt={sponsor.name}
              className={`object-contain ${size === "large" ? "h-32" : "h-20"} max-w-full`}
            />
          ) : (
            <div className={`flex items-center justify-center ${size === "large" ? "h-32 text-3xl" : "h-20 text-xl"} font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {sponsor.name}
            </div>
          )}
          <p className={`mt-4 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{sponsor.name}</p>
          {sponsor.website_url && (
            <span className={`text-sm flex items-center gap-1 mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              <ExternalLink className="h-3 w-3" /> Bezoek website
            </span>
          )}
        </CardContent>
      </Card>
    </a>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse">
          <div className={`h-10 w-48 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded mb-8`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={`h-40 ${isDark ? 'bg-slate-800' : 'bg-slate-200'} rounded-lg`} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="sponsors-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>ONZE SPONSORS</h1>
          <p className={`mt-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Bedankt aan onze partners die Futsal Gent mogelijk maken
          </p>
        </div>

        {/* Gold Sponsors */}
        {goldSponsors.length > 0 && (
          <section className="mb-12">
            <h2 className={`text-xl font-bold mb-6 text-center ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-600'}`}>
              ★ Gouden Partners ★
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {goldSponsors.map((sponsor) => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} size="large" />
              ))}
            </div>
          </section>
        )}

        {/* Silver Sponsors */}
        {silverSponsors.length > 0 && (
          <section className="mb-12">
            <h2 className={`text-lg font-bold mb-6 text-center ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
              Zilveren Partners
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {silverSponsors.map((sponsor) => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} />
              ))}
            </div>
          </section>
        )}

        {/* Bronze Sponsors */}
        {bronzeSponsors.length > 0 && (
          <section className="mb-12">
            <h2 className={`text-lg font-bold mb-6 text-center ${isDark ? 'text-amber-700' : 'text-amber-800'}`}>
              Bronzen Partners
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {bronzeSponsors.map((sponsor) => (
                <SponsorCard key={sponsor.id} sponsor={sponsor} />
              ))}
            </div>
          </section>
        )}

        {sponsors.length === 0 && (
          <div className="text-center py-16">
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
              Sponsorinformatie binnenkort beschikbaar.
            </p>
          </div>
        )}

        {/* Word een sponsor CTA */}
        <section className={`mt-16 p-8 rounded-lg text-center ${isDark ? 'bg-slate-900/50 border border-white/10' : 'bg-white border border-slate-200 shadow-sm'}`}>
          <h3 className={`text-2xl font-bold mb-4 font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Word een sponsor
          </h3>
          <p className={`mb-6 max-w-2xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Geïnteresseerd in samenwerking met Futsal Gent? Word onderdeel van onze gemeenschap van sponsors en help ons de sport futsal in België te laten groeien.
          </p>
          <a 
            href="/contact" 
            className="inline-block px-6 py-3 bg-[hsl(217,91%,60%)] text-white font-medium rounded-lg hover:bg-[hsl(217,91%,50%)] transition-colors"
          >
            Neem contact op
          </a>
        </section>
      </div>
    </div>
  );
}
